import { RoundMinutesPipe } from 'src/app/shared/pipes/round-minutes.pipe'
import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

import {
  EmployeeBooking,
  EmployeeBookingActionParams,
  EmployeeBookingActionTypes,
  InvoiceText
} from './employee-booking'
import { EmployeeBookingSerializer } from './employee-booking.serializer'
import { EmployeeContract } from '../employee-contract/employee-contract'
import moment from 'moment-mini'
import { Observable } from 'rxjs'
import { Dictionary } from '@ngrx/entity'
import { Product } from '../../product/product'
import { EmployeeContractManagerLink } from '../employee-contract-manager-link/employee-contract-manager-link'
import { take } from 'rxjs/operators'
import { FormatDatePipe } from 'src/app/shared/pipes/format-date.pipe'

@Injectable()
export class EmployeeBookingService extends ResourceService<EmployeeBooking> {
  constructor(
    private http: HttpClient,
    private employeeBookingSerializer: EmployeeBookingSerializer,
    private roundMinutes: RoundMinutesPipe,
    private dateFormatPipe: FormatDatePipe
  ) {
    super(
      http,
      `${environment.backendApiUrl}/employee-bookings`,
      employeeBookingSerializer
    )
  }

  public convertBookingColour(colourCode: number) {
    const red = colourCode & 0x000000ff
    const green = (colourCode & 0x0000ff00) >> 8
    const blue = (colourCode & 0x00ff0000) >> 16
    // const alpha = (colourCode & 0xff000000) >> 24

    return `rgb(${red},${green},${blue})`
  }

  public calculateInvoiceData(
    employeeBookings: EmployeeBooking[],
    contract: EmployeeContract,
    productEntities: Dictionary<Product>
  ): EmployeeBooking[] {
    const bookingsExtended = []
    bookingsExtended['subtotal'] = 0
    bookingsExtended['vat'] = 0
    bookingsExtended['inclVat'] = 0

    employeeBookings.map((booking) => {
      let bookingStartDate = moment(booking.startDate).format('d MMM yyyy')
      this.dateFormatPipe
        .transform(moment(booking.startDate)?.toISOString(), 'd MMM y')
        .pipe(take(1))
        .subscribe((formatDate) => {
          if (formatDate) {
            bookingStartDate = formatDate
          }
        })
      let description = `${
        productEntities[booking.productId]?.description
      }, ${bookingStartDate}, ${moment(booking.startDate).format(
        'HH:mm'
      )} - ${moment(
        moment(booking.endDate)
          .toDate()
          .setMinutes(
            this.roundMinutes.transform(
              moment(booking.endDate).toDate().getMinutes()
            )
          )
      ).format('HH:mm')}`
      if (booking?.description) {
        description = `${description} - ${booking.description}`
      }
      const bookingExtendedObj = {
        ...booking,
        description,
        durationInMinutes: moment
          .duration(moment(booking.endDate).diff(moment(booking.startDate)))
          .asMinutes()
      }

      bookingsExtended.push(bookingExtendedObj)

      const totalOfBooking =
        (bookingExtendedObj.durationInMinutes / 60) * booking.hourlyRate

      bookingsExtended['subtotal'] += totalOfBooking

      bookingsExtended['vat'] += totalOfBooking * (contract.vat / 100)
    })

    bookingsExtended['inclVat'] =
      bookingsExtended['subtotal'] + bookingsExtended['vat']

    return bookingsExtended
  }

  public runOneAction(
    actionType: EmployeeBookingActionTypes,
    memberId: number,
    contractId: number,
    params?: EmployeeBookingActionParams,
    invoiceText?: InvoiceText
  ): Observable<boolean> {
    return this.http.post<boolean>(
      `${environment.backendApiUrl}/employee-bookings/actions`,
      {
        personId: memberId,
        actionType,
        contractId,
        params,
        invoiceText
      }
    )
  }

  public showCancelButton(
    employeeBooking: EmployeeBooking,
    contractEntities: Dictionary<EmployeeContract>,
    employeeContractManagerLinkEntities: Dictionary<EmployeeContractManagerLink>
  ): boolean {
    if (!employeeBooking) {
      return false
    }
    let firstSupervisorCheck = null
    let lastChangedDate = null

    const employeeContract = contractEntities[employeeBooking.contractId]

    employeeContract?.employeeContractManagerLinks?.forEach(
      (employeeContractManagerLinkId) => {
        if (
          moment(
            employeeContractManagerLinkEntities[employeeContractManagerLinkId]
              ?.checkedTillDate
          ).isBefore(moment(firstSupervisorCheck)) ||
          !firstSupervisorCheck
        ) {
          firstSupervisorCheck =
            employeeContractManagerLinkEntities[employeeContractManagerLinkId]
              ?.checkedTillDate
        }

        if (
          moment(
            employeeContractManagerLinkEntities[employeeContractManagerLinkId]
              ?.updatedAt
          ).isAfter(moment(lastChangedDate)) ||
          !lastChangedDate
        ) {
          lastChangedDate =
            employeeContractManagerLinkEntities[employeeContractManagerLinkId]
              ?.updatedAt
        }
      }
    )

    if (!firstSupervisorCheck) {
      return true
    }

    if (!employeeContract?.endDate) {
      return moment(employeeBooking.startDate).isAfter(
        moment(firstSupervisorCheck)
      )
    }

    if (
      moment(firstSupervisorCheck).valueOf() !=
        moment(employeeContract?.endDate).valueOf() &&
      moment(employeeBooking.startDate).isBetween(
        moment(firstSupervisorCheck),
        moment(employeeContract?.endDate)
      )
    ) {
      return true
    }

    if (
      moment(firstSupervisorCheck).valueOf() ==
        moment(employeeContract?.endDate).valueOf() &&
      moment(lastChangedDate).clone().add(2, 'month').isSameOrAfter(moment())
    ) {
      return true
    }
    return false
  }

  public getAgendaCloseDate(
    employeeContract: EmployeeContract,
    employeeContractManagerLinkEntities: Dictionary<EmployeeContractManagerLink>
  ) {
    let firstSupervisorCheck = null

    employeeContract?.employeeContractManagerLinks?.forEach(
      (employeeContractManagerLinkId) => {
        if (
          moment(
            employeeContractManagerLinkEntities[employeeContractManagerLinkId]
              ?.checkedTillDate
          ).isBefore(moment(firstSupervisorCheck)) ||
          !firstSupervisorCheck
        ) {
          firstSupervisorCheck =
            employeeContractManagerLinkEntities[employeeContractManagerLinkId]
              ?.checkedTillDate
        }
      }
    )

    return firstSupervisorCheck
  }
}
